import { CONSENT_EVENT, isLikelyRegulatedRegion, readConsentChoice } from '~/utils/consent'

const ATTRIBUTION_COOKIE_DAYS = 90
// PPC margin token (ptok) only applies to the landing session, not the 90-day
// attribution window — the served price must not change days after the click.
const PPC_TOKEN_COOKIE_HOURS = 24
const ATTRIBUTION_QUERY_PARAMS = ['gclid', 'gclsrc', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ptok']
const PENDING_STORAGE_KEY = '_tripening_attr_pending'

/**
 * Captures Google Ads click identifiers and UTM parameters from the URL
 * and persists them in first-party cookies for 90 days (the PPC margin
 * token `ptok` for 24 hours — see PPC_TOKEN_COOKIE_HOURS).
 *
 * Uses window.location.search directly (not Vue Router) to guarantee
 * the raw URL params are available regardless of hydration timing.
 *
 * Consent Mode v2: in regulated regions (see utils/consent.ts) without a
 * stored consent choice, captured params are held in sessionStorage instead
 * of cookies. On "Accept all" the stash is flushed to cookies; on reject it
 * is discarded. Outside regulated regions behavior is unchanged.
 */
export default defineNuxtPlugin(() => {
  captureAttribution()

  const router = useRouter()
  router.afterEach((to) => {
    if (ATTRIBUTION_QUERY_PARAMS.some((key) => to.query[key])) {
      captureAttribution()
    }
  })

  window.addEventListener(CONSENT_EVENT, ((event: CustomEvent) => {
    if (event.detail?.choice === 'granted') {
      flushPendingAttribution()
    } else {
      discardPendingAttribution()
    }
  }) as EventListener)
})

function attributionCookiesAllowed(): boolean {
  if (!isLikelyRegulatedRegion()) return true
  return readConsentChoice() === 'granted'
}

function captureAttribution() {
  const params = new URLSearchParams(window.location.search)
  const gclid = params.get('gclid')
  const gclsrc = params.get('gclsrc')

  const isGoogleAds = !gclsrc || gclsrc.includes('aw')

  const captured: Record<string, string> = {}

  if (gclid && isGoogleAds) {
    captured._tripening_gclid = gclid
    if (gclsrc) {
      captured._tripening_gclsrc = gclsrc
    }
  }

  for (const param of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const value = params.get(param)
    if (value) {
      captured[`_tripening_${param}`] = value
    }
  }

  // Signed PPC campaign token — forwarded as X-PPC-Campaign so ETL applies the
  // campaign's channel/margin rules to this session's prices. Same consent
  // routing as the other attribution cookies, but a 24h lifetime.
  const ppcToken = params.get('ptok')
  if (ppcToken) {
    captured._tripening_ppc = ppcToken
  }

  if (Object.keys(captured).length === 0) {
    return
  }

  if (attributionCookiesAllowed()) {
    for (const [name, value] of Object.entries(captured)) {
      setCookie(name, value, cookieHours(name))
    }
    return
  }

  // Regulated region, no consent yet (denied users discard on the consent
  // event, so a lingering stash is only ever pre-choice): hold in
  // sessionStorage until the banner resolves.
  try {
    const existing = JSON.parse(sessionStorage.getItem(PENDING_STORAGE_KEY) || '{}')
    sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify({ ...existing, ...captured }))
  } catch {
    // sessionStorage unavailable (private mode edge cases) — drop silently.
  }
}

function flushPendingAttribution() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_STORAGE_KEY) || '{}')
    for (const [name, value] of Object.entries(pending)) {
      if (typeof value === 'string' && name.startsWith('_tripening_')) {
        setCookie(name, value, cookieHours(name))
      }
    }
    sessionStorage.removeItem(PENDING_STORAGE_KEY)
  } catch {
    // Ignore malformed stash.
  }
}

function discardPendingAttribution() {
  try {
    sessionStorage.removeItem(PENDING_STORAGE_KEY)
  } catch {
    // Ignore.
  }
}

function cookieHours(name: string): number {
  return name === '_tripening_ppc' ? PPC_TOKEN_COOKIE_HOURS : ATTRIBUTION_COOKIE_DAYS * 24
}

function setCookie(name: string, value: string, hours: number = ATTRIBUTION_COOKIE_DAYS * 24) {
  const expires = new Date()
  expires.setTime(expires.getTime() + hours * 60 * 60 * 1000)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}
